import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { NumericInput } from '../../../../../components/shared/ui/NumericInput';
import { useAppDispatch, useAppSelector } from '../../../../../store';
import {
  addNetsuiteItem,
  removeNetsuiteItem,
  updateNetsuiteItemQuantity,
} from '../../../../../store/slices/createCRSlice';
import { selectClientNetsuiteItems } from '../../../../../store/selectors/createCRSelectors';
import { NetsuiteItemResponse } from '../../../../../types/netsuite-items.types';
import { SearchDropdown } from '../../../../../components/shared/ui/SearchDropdown';
import { mapNetsuiteResponseToState } from '../../../../../utils/enphase-netsuite-items.utils';

interface ClientPackagesSectionProps {
  clientId: string;
  clientName: string;
  isEnabled: boolean;
  /** Client NetSuite catalog (fetched once by the parent, shared with the Enphase section) */
  catalogItems: NetsuiteItemResponse[];
  isCatalogLoading: boolean;
}

export const ClientPackagesSection: React.FC<ClientPackagesSectionProps> = ({
  clientId,
  clientName,
  isEnabled,
  catalogItems,
  isCatalogLoading,
}) => {
  const dispatch = useAppDispatch();
  const selectedItems = useAppSelector(selectClientNetsuiteItems);

  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedManufacturer, setSelectedManufacturer] = useState<string | null>(null);

  const allItems = catalogItems;
  const isLoading = isCatalogLoading;

  // Auto-added Enphase items render in their own section — hide them here
  const visibleSelectedItems = useMemo(
    () => selectedItems.filter(item => !item.auto_added),
    [selectedItems],
  );

  // Reset manufacturer when category changes
  useEffect(() => {
    setSelectedManufacturer(null);
  }, [selectedCategory]);

  const categories = useMemo(
    () => Array.from(new Set(allItems.map(i => i.item_category).filter(Boolean))).sort(),
    [allItems],
  );

  const manufacturers = useMemo(() => {
    const scoped = selectedCategory ? allItems.filter(i => i.item_category === selectedCategory) : allItems;
    return Array.from(new Set(scoped.map(i => i.manufacturer).filter((m): m is string => !!m))).sort();
  }, [allItems, selectedCategory]);

  const selectedIds = useMemo(() => new Set(selectedItems.map(i => i.id)), [selectedItems]);

  const categoryOptions = useMemo(() => categories.map(c => ({ id: c, label: c })), [categories]);
  const manufacturerOptions = useMemo(() => manufacturers.map(m => ({ id: m, label: m })), [manufacturers]);

  const hasSearch = search.trim().length > 0;
  const hasFilters = !!selectedCategory || !!selectedManufacturer;
  const showResults = hasSearch || hasFilters;

  const filteredItems = useMemo(() => {
    if (!showResults) return [];
    const query = search.trim().toLowerCase();
    return allItems.filter(item => {
      if (selectedIds.has(item.id)) return false;
      if (selectedCategory && item.item_category !== selectedCategory) return false;
      if (selectedManufacturer && item.manufacturer !== selectedManufacturer) return false;
      if (query && !item.name?.toLowerCase().includes(query) && !item.external_id?.toLowerCase().includes(query) && !item.display_name?.toLowerCase().includes(query)) return false;
      return true;
    });
  }, [allItems, search, selectedCategory, selectedManufacturer, selectedIds, showResults]);

  const handleCategorySelect = useCallback((option: { id: string; label: string } | null) => {
    setSelectedCategory(option?.id ?? null);
  }, []);

  const handleManufacturerSelect = useCallback((option: { id: string; label: string } | null) => {
    setSelectedManufacturer(option?.id ?? null);
  }, []);

  const handleAddItem = useCallback((item: NetsuiteItemResponse) => {
    dispatch(addNetsuiteItem(mapNetsuiteResponseToState(item, { quantity: 1 })));
  }, [dispatch]);

  const handleRemoveItem = useCallback((id: string) => {
    dispatch(removeNetsuiteItem(id));
  }, [dispatch]);

  const handleQuantityChange = useCallback((id: string, quantity: number) => {
    dispatch(updateNetsuiteItemQuantity({ id, quantity, markEdited: true }));
  }, [dispatch]);

  const labelClass = 'block text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1';

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200/80 overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-2 3xl:px-5 bg-blue-50 border-b border-blue-100">
        <svg className="w-3.5 h-3.5 text-blue-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
        </svg>
        <span className="text-[10px] 3xl:text-[11px] uppercase tracking-widest font-bold text-slate-700 shrink-0">Client Database</span>
        <span className="ml-auto rounded-full border border-blue-200 bg-white px-2 py-0.5 text-xs font-semibold text-blue-700 shrink-0">
          {clientName}
        </span>
      </div>

      <div className="p-4">
        {/* ── Section 1: Search & Filters ── */}
        <div className="pb-3 border-b border-slate-100">
          <div className="flex items-center gap-3">
            {/* Name search — takes more space */}
            <div className="flex-1">
              <label className={labelClass}>Search</label>
              <div className="relative">
                <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-300 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={`Search ${clientName} items by name or ID...`}
                  className="w-full pl-7 pr-3 py-1 text-[11px] rounded-lg border border-slate-200 bg-slate-50/50 text-slate-600 placeholder-slate-300 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 focus:bg-white transition-all"
                />
              </div>
            </div>
            {/* Category */}
            <div className="w-40">
              <label className={labelClass}>Category</label>
              <SearchDropdown
                options={categoryOptions}
                onSelect={handleCategorySelect}
                value={selectedCategory ? { id: selectedCategory, label: selectedCategory } : null}
                placeholder={isLoading ? 'Loading...' : 'All'}
                isLoading={isLoading}
                disabled={!isEnabled || isLoading}
                enableArrowNavigation
                inputClassName="!py-1 !px-2 !text-[11px]"
              />
            </div>
            {/* Manufacturer */}
            <div className="w-40">
              <label className={labelClass}>Manufacturer</label>
              <SearchDropdown
                options={manufacturerOptions}
                onSelect={handleManufacturerSelect}
                value={selectedManufacturer ? { id: selectedManufacturer, label: selectedManufacturer } : null}
                placeholder="All"
                disabled={!isEnabled}
                enableArrowNavigation
                inputClassName="!py-1 !px-2 !text-[11px]"
              />
            </div>
          </div>
        </div>

        {/* ── Section 2: Search Results ── */}
        {showResults && (
          <div className="py-3 border-b border-slate-100">
            <div className="flex items-center justify-between mb-1.5">
              <label className={labelClass}>
                Results
                <span className="ml-1.5 text-slate-400 normal-case tracking-normal font-normal">
                  ({filteredItems.length})
                </span>
              </label>
            </div>
            {filteredItems.length === 0 ? (
              <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50/50 py-3 text-center">
                <p className="text-[10px] text-slate-400">
                  {isLoading ? 'Loading...' : 'No items matching your search'}
                </p>
              </div>
            ) : (
              <div className="rounded-lg border border-slate-200 overflow-hidden max-h-80 3xl:max-h-96 overflow-y-auto">
                {filteredItems.map((item, idx) => (
                  <div
                    key={item.id}
                    className={`flex items-center gap-2 px-3 py-1 transition-colors ${
                      idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'
                    } hover:bg-blue-50/30 border-b border-slate-100/80 last:border-b-0`}
                  >
                    <div className="flex-1 min-w-0">
                      <span className="font-mono text-[11px] text-blue-700 font-semibold mr-1.5">
                        {item.external_id}
                      </span>
                      <span className="text-[11px] text-slate-600">
                        {item.name}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleAddItem(item)}
                      disabled={!isEnabled}
                      className="shrink-0 w-5 h-5 rounded flex items-center justify-center bg-blue-100 text-blue-600 hover:bg-blue-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      title="Add to selection"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Section 3: Selected Items ── */}
        {visibleSelectedItems.length > 0 && (
          <div className="pt-3">
            <div className="flex items-center justify-between mb-1.5">
              <label className={labelClass}>
                Selected
                <span className="ml-1.5 text-slate-400 normal-case tracking-normal font-normal">
                  ({visibleSelectedItems.length})
                </span>
              </label>
            </div>
            <div className="space-y-1.5">
              {visibleSelectedItems.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center gap-2 rounded-lg border border-emerald-100 bg-emerald-50/40 px-2.5 py-1.5 hover:bg-emerald-50/70 transition-colors group"
                >
                  <span className="shrink-0 font-mono text-[10px] font-bold text-emerald-700 bg-emerald-100 border border-emerald-200 rounded px-1 py-px">
                    {item.external_id}
                  </span>
                  <div className="flex-1 min-w-0 flex items-center gap-1.5">
                    <p className="text-[11px] font-medium text-slate-700 truncate leading-tight">
                      {item.name}
                    </p>
                    {item.item_category && (
                      <span className="shrink-0 text-[9px] text-slate-400 uppercase tracking-wider">
                        {item.item_category}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Qty</span>
                    <NumericInput
                      value={item.quantity}
                      onChange={(qty) => handleQuantityChange(item.id, qty)}
                      min={1} max={999}
                      disabled={!isEnabled}
                      className="w-11 rounded border border-emerald-200 bg-white px-1.5 py-0.5 text-[10px] text-center font-semibold text-slate-700 focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400 outline-none disabled:bg-slate-100 disabled:text-slate-400"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveItem(item.id)}
                    disabled={!isEnabled}
                    className="shrink-0 w-5 h-5 rounded flex items-center justify-center text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed opacity-0 group-hover:opacity-100"
                    title="Remove item"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty state */}
        {!showResults && visibleSelectedItems.length === 0 && !isLoading && allItems.length > 0 && (
          <p className="text-xs text-slate-400 text-center py-2">
            Search by name or filter by category/manufacturer
          </p>
        )}

        {!clientId && (
          <p className="text-xs text-slate-400 text-center py-2">
            No client selected
          </p>
        )}
      </div>
    </div>
  );
};
